import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, CircleHelp, Menu, ScrollText, Search, SunMoon } from "lucide-react";
import Logo from "@/components/Logo";
import ConfirmDialog from "../components/ConfirmDialog";
import DetailDrawer from "../components/DetailDrawer";
import Kbd from "../components/Kbd";
import Popover from "../components/Popover";
import ShortcutsHelp from "../components/ShortcutsHelp";
import { useAdmin } from "../lib/useAdmin";
import { useGlobalShortcuts } from "../lib/shortcuts";
import AdminNav, { ADMIN_NAV } from "./AdminNav";
import CommandPalette from "./CommandPalette";
import EnvBadge from "./EnvBadge";
import ImpersonationBanner from "./ImpersonationBanner";
import ThemeToggle, { useTheme } from "./ThemeToggle";
import "../admin.css";

/* Coquille de la console : bandeaux, barre latérale, barre du haut, contenu et
   panneau latéral optionnel. Elle est volontairement agnostique du routeur :
   `currentPath` et `onNavigate` sont fournis par l'écran qui la monte, ce qui
   permet de la brancher sur react-router comme de la rendre isolément. */
export default function AdminShell({
  title,
  subtitle,
  breadcrumb = [],
  actions,
  tabs = [],
  activeTab,
  onTabChange,
  children,
  panel = null,
  panelTitle,
  panelSubtitle,
  panelActions,
  onPanelClose,
  currentPath = "/admin",
  onNavigate,
  navCounts = {},
  searchSources = [],
  commandActions = [],
  onSearchFocus,
  env,
  documentTitle = true,
}) {
  const { admin, permissions, can, impersonation, stopImpersonation, reauthRequest, resolveReauth, cancelReauth } = useAdmin();
  const { mode, setMode } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [leavingImpersonation, setLeavingImpersonation] = useState(false);
  const mainRef = useRef(null);

  useEffect(() => {
    if (documentTitle && title) document.title = `${title} — Console Perform`;
  }, [documentTitle, title]);

  const navigate = useCallback((to, item) => {
    setNavOpen(false);
    onNavigate?.(to, item);
  }, [onNavigate]);

  const focusSearch = useCallback(() => {
    if (onSearchFocus) { onSearchFocus(); return; }
    const input = mainRef.current?.querySelector(".adm-field input");
    input?.focus();
    input?.select?.();
  }, [onSearchFocus]);

  const onEscape = useCallback(() => {
    if (paletteOpen) { setPaletteOpen(false); return; }
    if (helpOpen) { setHelpOpen(false); return; }
    if (navOpen) { setNavOpen(false); return; }
    if (panel && onPanelClose) onPanelClose();
  }, [paletteOpen, helpOpen, navOpen, panel, onPanelClose]);

  useGlobalShortcuts({
    onPalette: () => setPaletteOpen(true),
    onGo: (key, target) => navigate(target.to),
    onSearch: focusSearch,
    onHelp: () => setHelpOpen(true),
    onEscape,
  });

  // Actions toujours disponibles, complétées par celles de l'écran courant.
  const shellActions = useMemo(() => {
    const goTo = ADMIN_NAV.flatMap(group => group.items)
      .filter(item => can(item.perm))
      .map(item => ({
        id: `go-${item.key}`,
        label: item.label,
        group: "Aller à",
        icon: item.icon,
        keywords: item.to,
        run: () => navigate(item.to, item),
      }));
    return [
      ...commandActions,
      {
        id: "theme",
        label: "Basculer le thème (clair, sombre, système)",
        group: "Console",
        icon: SunMoon,
        keywords: "thème sombre clair dark mode",
        run: () => setMode(mode === "light" ? "dark" : mode === "dark" ? "system" : "light"),
      },
      {
        id: "shortcuts",
        label: "Afficher les raccourcis clavier",
        group: "Console",
        icon: CircleHelp,
        shortcut: "?",
        run: () => setHelpOpen(true),
      },
      ...goTo,
    ];
  }, [commandActions, can, navigate, mode, setMode]);

  const stop = async () => {
    setLeavingImpersonation(true);
    try {
      await stopImpersonation();
    } finally {
      setLeavingImpersonation(false);
    }
  };

  const initials = (admin?.name || admin?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="adm">
      <a className="adm-skip" href="#adm-main">Aller au contenu</a>

      <div className="adm-bands">
        <EnvBadge env={env} variant="band" />
        <ImpersonationBanner impersonation={impersonation} onStop={stop} busy={leavingImpersonation} />
      </div>

      <div className={`adm-frame${panel ? " has-panel" : ""}`}>
        {navOpen && <button type="button" className="adm-scrim" aria-label="Fermer la navigation" onClick={() => setNavOpen(false)} />}

        <aside className={`adm-side${navOpen ? " is-open" : ""}`} data-testid="admin-sidebar">
          <div className="adm-side-head">
            <Logo />
            <span className="adm-side-tag">Console</span>
          </div>

          <button type="button" className="adm-side-search" onClick={() => setPaletteOpen(true)} data-testid="open-palette">
            <Search size={14} />
            <span>Rechercher…</span>
            <Kbd combo="mod+k" />
          </button>

          <div className="adm-side-scroll">
            <AdminNav
              currentPath={currentPath}
              onNavigate={navigate}
              counts={navCounts}
              onItemClick={() => setNavOpen(false)}
            />
          </div>

          <div className="adm-side-foot">
            <span className="adm-avatar" aria-hidden="true">{initials}</span>
            <div>
              <strong>{admin?.name || "Administrateur"}</strong>
              <small>{admin?.role || `${permissions.length} permission(s)`}</small>
            </div>
          </div>
        </aside>

        <div className="adm-col">
          <header className="adm-top">
            <button
              type="button"
              className="adm-btn is-ghost is-icon adm-burger"
              onClick={() => setNavOpen(true)}
              aria-label="Ouvrir la navigation"
              data-testid="open-nav"
            >
              <Menu size={16} />
            </button>

            <nav className="adm-crumbs" aria-label="Fil d'Ariane">
              {breadcrumb.map(crumb => (
                <span key={crumb.label} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                  {crumb.to
                    ? <a href={crumb.to} onClick={e => { e.preventDefault(); navigate(crumb.to); }}>{crumb.label}</a>
                    : <span>{crumb.label}</span>}
                  <ChevronRight size={12} />
                </span>
              ))}
              <b>{title}</b>
            </nav>

            <span className="adm-top-spacer" />

            <div className="adm-top-actions">
              <EnvBadge env={env} />
              <ThemeToggle mode={mode} onChange={setMode} />
              <button
                type="button"
                className="adm-btn is-ghost is-icon"
                onClick={() => setHelpOpen(true)}
                aria-label="Raccourcis clavier"
                title="Raccourcis clavier (?)"
                data-testid="open-shortcuts"
              >
                <CircleHelp size={15} />
              </button>
              <Popover
                testid="admin-menu"
                title="Compte d'administration"
                chevron={false}
                triggerClass="adm-btn is-ghost is-icon"
                label={<span className="adm-avatar">{initials}</span>}
                width={252}
              >
                {({ close }) => (
                  <>
                    <div className="adm-menu-head"><span>{admin?.email || "Session d'administration"}</span></div>
                    <div className="adm-menu-empty" style={{ paddingTop: 0 }}>
                      {admin?.role ? `Rôle : ${admin.role}. ` : ""}{permissions.length} permission(s) accordée(s).
                    </div>
                    <div className="adm-menu-sep" />
                    <button
                      type="button"
                      className="adm-menu-item"
                      onClick={() => { close(); navigate("/admin/audit?acteur=moi"); }}
                      data-testid="menu-my-audit"
                    >
                      <ScrollText size={14} /><span>Mes actions dans l'audit</span>
                    </button>
                    <button
                      type="button"
                      className="adm-menu-item"
                      onClick={() => { close(); setHelpOpen(true); }}
                    >
                      <CircleHelp size={14} /><span>Raccourcis clavier</span>
                    </button>
                  </>
                )}
              </Popover>
            </div>
          </header>

          <main className="adm-main" id="adm-main" ref={mainRef} tabIndex={-1}>
            <div className="adm-page">
              <div className="adm-page-head">
                <div>
                  <h1>{title}</h1>
                  {subtitle && <p>{subtitle}</p>}
                </div>
                {actions && <div className="adm-page-actions">{actions}</div>}
              </div>

              {tabs.length > 0 && (
                <div className="adm-tabs" role="tablist" aria-label="Vues de la page">
                  {tabs.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={tab.id === activeTab}
                      className={tab.id === activeTab ? "is-active" : undefined}
                      onClick={() => onTabChange?.(tab.id)}
                      data-testid={`tab-${tab.id}`}
                    >
                      {tab.label}{tab.count !== undefined && <span>{tab.count}</span>}
                    </button>
                  ))}
                </div>
              )}

              {children}
            </div>
          </main>
        </div>

        {panel && (
          <aside className="adm-panel" data-testid="admin-panel">
            <DetailDrawer
              open
              variant="docked"
              title={panelTitle}
              subtitle={panelSubtitle}
              actions={panelActions}
              onClose={onPanelClose}
              testid="admin-panel-drawer"
            >
              {panel}
            </DetailDrawer>
          </aside>
        )}
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        searchSources={searchSources}
        actions={shellActions}
        onNavigate={navigate}
      />

      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Dialogue global de ré-authentification : déclenché par withReauth()
          depuis n'importe quel appel d'API, pas seulement depuis un écran. */}
      <ConfirmDialog
        open={Boolean(reauthRequest)}
        onClose={cancelReauth}
        onConfirm={granted => resolveReauth(granted)}
        level="reauth"
        tone="default"
        title="Confirmez votre identité"
        description={reauthRequest?.error?.message || "Cette action exige une ré-authentification avant d'être rejouée."}
        confirmLabel="Confirmer et poursuivre"
        testid="reauth-dialog"
      />
    </div>
  );
}
